import { InvoiceChargeType, InvoiceSummaryType, NotificationContextType } from "../../types";
import { Box, Divider, VStack, Text, Select, Card, CardBody, HStack, Stack, Badge, SimpleGrid, StatLabel, Stat, StatNumber } from "@chakra-ui/react";
import InvoiceChargesGrid from "./InvoiceChargesSection";
import InvoiceReadsSection from "./InvoiceReadsSection";
import { useContext, useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { NotificationContext } from "../../components/NotificationPane";

type InvoiceProps = {
  invoice: InvoiceSummaryType,
  supply_number: string
}

function InvoiceDetail({invoice}: InvoiceProps) {

    const { ready, getInvoiceCharges } = useApi();
    const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
    const [invoiceCharges, setInvoiceCharges] = useState<InvoiceChargeType[] | undefined>(undefined);
    const [selectedSupply, setSelectedSupply] = useState<string>(invoice.supply_number);

    useEffect(() => {
        async function loadInvoice() {
            try {
                const response = await getInvoiceCharges(invoice.invoice_number, invoice.supplier_code);
                setInvoiceCharges(response);
            } catch(err) {
                pushNotification("Could not load invoice.", "error")
            }
        }
        if (ready && invoice)
            loadInvoice();
    }, [ready, invoice]);

    

    if (!invoice || !invoiceCharges) return <div>Loading...</div>

    const supplyNumbers = Array.from(new Set(invoiceCharges.map(d => d.supply_number)));
    const filteredInvoiceCharges = invoiceCharges.filter(d => d.supply_number === selectedSupply);

    return (
        <VStack margin={8} spacing={4} align="stretch">
            <Box w="100%">
                <Card>
                    <CardBody>
                        <HStack justify="space-between">
                            <Stack spacing={0}>
                                <Text fontWeight="semibold">Invoice: {invoice.invoice_number}</Text>
                                <HStack>
                                <Text fontSize="sm">Supply:</Text>
                                <Select
                                    id="supply-select"
                                    size={'small'}
                                    value={selectedSupply}
                                    onChange={e => setSelectedSupply(e.target.value)}
                                    style={{ marginLeft: 8, padding: 4, minWidth: 150 }}
                                >
                                    {supplyNumbers.map(sn => (
                                        <option key={sn} value={sn}>{sn}</option>
                                    ))}
                                </Select>
                                </HStack>
                                <Text fontSize="sm" color="gray.500">Supplier: {invoice.supplier_code}</Text>
                            </Stack>
                            {invoice.validation === "PASS" ? (
                                <Badge colorScheme="green" variant="subtle">✓ All checks passed</Badge>
                            ) : invoice.validation === "FAIL" ? (
                                <Badge colorScheme="red" variant="subtle">✗ Checks failed</Badge>
                            ) : null}
                        </HStack>
                        <Divider />
                        <SimpleGrid columns={2} spacing={6} mt={6}>
                            <Stat>
                                <StatLabel>Total due <i>(for this supply)</i></StatLabel>
                                <StatNumber>£{invoice.subtotal_w_vat.toFixed(2)}</StatNumber>
                                <StatNumber fontSize="sm" color="gray.500">£{invoice.subtotal.toFixed(2)} excl. VAT</StatNumber>
                            </Stat>

                            <Stat>
                                <StatLabel>Billing period</StatLabel>
                                <StatNumber>{formatDateRange(new Date(invoice.period_start), new Date(invoice.period_end))}</StatNumber>
                            </Stat>
                        </SimpleGrid>
                    </CardBody>
                </Card>
            </Box>
            <Divider />
            <Box w="100%">
                <Text fontSize="sm" color="gray.500" mb={1} ml={1} fontWeight="semibold">Charges (excl. VAT)</Text>
                <Box w="100%">
                    <InvoiceChargesGrid invoice_charges={filteredInvoiceCharges} />
                </Box>
            </Box>
            <Divider />
            <Box w="100%">
                <Text fontSize="sm" color="gray.500" mb={1} ml={1} fontWeight="semibold">Reads</Text>
                <Box w="100%">
                    <InvoiceReadsSection invoice_number={invoice.invoice_number} supplier_code={invoice.supplier_code} supply_number={selectedSupply} />
                </Box>
            </Box>    
        </VStack>
    )
}

export default InvoiceDetail


function formatDateRange(start: Date, end: Date): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const sDay = start.getDate();
  const sMonth = months[start.getMonth()];
  const sYear = start.getFullYear();
  const eDay = end.getDate();
  const eMonth = months[end.getMonth()];
  const eYear = end.getFullYear();

  if (sYear === eYear && sMonth === eMonth) {
    // Same month and year
    return `${sDay}–${eDay} ${eMonth} ${eYear}`;
  } else if (sYear === eYear) {
    // Same year, different months
    return `${sDay} ${sMonth}–${eDay} ${eMonth} ${eYear}`;
  } else {
    // Different years
    return `${sDay} ${sMonth} ${sYear}–${eDay} ${eMonth} ${eYear}`;
  }
}